import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Link2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || 'https://saanjh.cc';

const StoreSlug = () => {
  const { vendor } = useAuth();
  const [loading, setLoading] = useState(false);
  const [newSlug, setNewSlug] = useState('');

  const currentUrl = `${FRONTEND_URL}/store/${vendor?.slug}`;

  const handleUpdateSlug = async (e) => {
    e.preventDefault();
    
    if (!newSlug.trim()) {
      toast.error('Please enter a new store slug');
      return;
    }

    // Validate slug format
    if (!/^[a-z0-9-]+$/.test(newSlug)) {
      toast.error('Slug can only contain lowercase letters, numbers, and hyphens');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('vendor_token');
      const response = await axios.put(
        `${API}/vendor/slug`,
        { new_slug: newSlug },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Store slug updated successfully! 🎉', {
        description: 'Your QR code has also been updated with the new link.',
      });
      
      setNewSlug('');
      
      // Reload to update the sidebar with new slug
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update slug';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="store-slug-page">
      <div className="mb-8">
        <h2 className="text-3xl font-bold tracking-tight">Store Link</h2>
        <p className="text-muted-foreground mt-2">Customize your store URL</p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Current Store Link</CardTitle>
          <CardDescription>This is your current storefront URL</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 p-4 bg-secondary/50 rounded-lg">
            <Link2 className="h-5 w-5 text-muted-foreground" />
            <div className="flex-1">
              <p className="font-mono text-sm break-all">{currentUrl}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(currentUrl);
                toast.success('Link copied to clipboard');
              }}
            >
              Copy
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Change Store Slug</CardTitle>
          <CardDescription>Update your store URL (allowed once per month)</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpdateSlug} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-slug">New Slug</Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <div className="flex items-center">
                    <span className="px-3 py-2 bg-secondary text-muted-foreground border border-r-0 rounded-l-md text-sm">
                      {FRONTEND_URL}/store/
                    </span>
                    <Input
                      id="new-slug"
                      value={newSlug}
                      onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      placeholder="my-awesome-store"
                      className="rounded-l-none"
                      data-testid="new-slug-input"
                    />
                  </div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Only lowercase letters, numbers, and hyphens allowed
              </p>
            </div>

            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-amber-900 font-medium">Important Notes:</p>
                <ul className="text-xs text-amber-800 mt-1 space-y-1">
                  <li>• You can only change your slug once every 30 days</li>
                  <li>• Your QR code will be automatically regenerated</li>
                  <li>• Old links will no longer work after change</li>
                  <li>• Make sure to inform your customers about the new link</li>
                </ul>
              </div>
            </div>

            <Button type="submit" disabled={loading || !newSlug} data-testid="update-slug-btn">
              {loading ? 'Updating...' : 'Update Store Link'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default StoreSlug;